"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";

import NoDataImage from "@/assets/error/no-data-found.jpg";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import useGetServiceInterfaces from "@/hooks/requests/service-interface/use-get-service-interfaces";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { IServiceInterface } from "@/interfaces/service-interface.interface";
import { ServiceInterfaceMessages } from "@/messages/service-interface/service-interface.messages";

import FiltersForm from "./components/FiltersForm";
import { ServiceInterfaceTable } from "./components/Table";
import { initialFilterValues } from "./constants";

type Props = {
  messages: ServiceInterfaceMessages;
};

const FIRST_PAGE = 0;
const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;

function ServiceInterfacesPage({ messages }: Props) {
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters } = useFilters<IServiceInterface>({
    initialValue: initialFilterValues,
  });

  const { data, isLoading, error } = useGetServiceInterfaces({ filters, pageNumber, pageSize });

  const shouldLoadTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !isLoading && !!error;
  const shouldLoadNoData = !!data && !isLoading && !error && data.items.length === 0;

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(newFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(newFilters);
          }}
        >
          <FiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense isSuspended={isLoading} fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}>
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.tableTitle}
                </MDTypography>
              </MDBox>

              {shouldLoadTable && (
                <ServiceInterfaceTable pagination={pagination} data={data} messages={messages} />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoad}
                  description={error.message}
                  src={NoDataImage}
                  alt={messages.noDataFoundAlt}
                />
              )}

              {shouldLoadNoData && (
                <AlertBlock
                  title={messages.noDataFoundError}
                  src={NoDataImage}
                  alt={messages.noDataFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default ServiceInterfacesPage;
